import debounce from 'lodash/debounce';
import { action, extendObservable, reaction } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import { ContentFilteringStore } from '../BrowserPolicies/ContentFilteringStore';
import { DataLossPreventionStore } from '../BrowserPolicies/DLPStore';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import SelectStore from '../SelectStore';
import UrlBulkEditorCardStore from '../UrlBulkEditorCardStore';
import { lenAwareEmailValidator } from '../../email';
import AsyncActionStore from '../AsyncActionStore';
import { apiCall } from '../../API';

const TIMEOUT_OPTIONS = [
    { value: 60, label: '1 Minute' },
    { value: 180, label: '3 Minutes' },
    { value: 300, label: '5 Minutes' },
    { value: 420, label: '7 Minutes' },
    { value: 600, label: '10 Minutes' },
    { value: 900, label: '15 Minutes' },
    { value: 1800, label: '30 Minutes' },
];

const DEFAULT_TIMEOUT = 420;

function compareArrays(arr1, arr2) {
    if (arr1.length !== arr2.length) {
        return false;
    }
    return arr1.every((x) => arr2.indexOf(x) !== -1);
}

function userGroupSortFn(a, b) {
    if (a.toLowerCase().startsWith('pfpt:vipusers')) {
        return -1;
    }
    if (b.toLowerCase().startsWith('pfpt:vipusers')) {
        return 1;
    }
    if (a.toLowerCase().startsWith('pfpt:vapusers')) {
        return -1;
    }
    if (b.toLowerCase().startsWith('pfpt:vapusers')) {
        return 1;
    }
    return a.localeCompare(b);
}

export default class UDPolicyEditStore {
    static PropType = PropTypes.shape({
        isNew: PropTypes.bool.isRequired,
        viewMode: PropTypes.bool.isRequired,

        modal: FlagStore.PropType.isRequired,
        exitConfirmationModal: FlagStore.PropType.isRequired,

        isDefault: PropTypes.bool.isRequired,

        policyDefinitionToggle: FlagStore.PropType.isRequired,
        nameStore: InputStore.PropType.isRequired,
        enabledStore: FlagStore.PropType.isRequired,

        userGroupsError: PropTypes.string,
        userGroupsToggle: FlagStore.PropType.isRequired,
        userGroupFilterStore: InputStore.PropType.isRequired,
        unselectedUserGroups: MobxPropTypes.arrayOrObservableArray,
        selectedUserGroups: MobxPropTypes.arrayOrObservableArray,

        redirectOptionsToggle: FlagStore.PropType.isRequired,
        isolatedCategoriesFilterStore: InputStore.PropType.isRequired,
        unselectedIsolatedCategories: MobxPropTypes.arrayOrObservableArray,
        selectedIsolatedCategories: MobxPropTypes.arrayOrObservableArray,
        unselectedIsolatedCategoriesCount: PropTypes.number.isRequired,
        selectedIsolatedCategoriesCount: PropTypes.number.isRequired,
        neverIsolate: UrlBulkEditorCardStore.PropType.isRequired,

        securityOptionsToggle: FlagStore.PropType.isRequired,
        displayExitWarningStore: FlagStore.PropType.isRequired,
        exitWarningTextStore: InputStore.PropType.isRequired,

        dlpToggle: FlagStore.PropType.isRequired,
        dlpStore: DataLossPreventionStore.PropType.isRequired,

        contentFilteringToggle: FlagStore.PropType.isRequired,
        contentFilteringStore: ContentFilteringStore.PropType.isRequired,

        adminOptionsToggle: FlagStore.PropType.isRequired,
        alwaysRedirectIgnoringScanStore: FlagStore.PropType.isRequired,
        scanTimeoutStore: SelectStore.PropType.isRequired,

        hasChanged: PropTypes.func.isRequired,
        closeModal: PropTypes.func.isRequired,
        redirectAllCategories: PropTypes.func.isRequired,
        removeAllCategories: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        this.ISOLATE_ALL_USERS = true;
        this.ISOLATE_SPECIFIC_USERS = false;

        extendObservable(this, /** @class UDPolicyEditStore */{
            isNew: false,
            viewMode: false,

            modal: new FlagStore(false),
            exitConfirmationModal: new FlagStore(false),

            id: null,
            isDefault: false,

            policyDefinitionToggle: new FlagStore(true),
            name: '',
            nameStore: new InputStore({ value: '' }),
            enabled: false,
            enabledStore: new FlagStore(false),

            userGroupsToggle: new FlagStore(true),
            userGroupFilterStore: new InputStore({ value: '' }),
            userGroupFilterByEmail: [],
            userGroupsError: null,
            allUserGroups: [],
            userGroups: [],
            originalUserGroups: [],
            get unselectedUserGroups() {
                if (this.userGroupFilterByEmail.length > 0) {
                    return this.allUserGroups
                        .filter((id) => this.userGroupFilterByEmail.indexOf(id) !== -1)
                        .filter((id) => this.userGroups.indexOf(id) === -1)
                        .sort(userGroupSortFn);
                }
                const filter = this.userGroupFilterStore.value.toLowerCase();
                return this.allUserGroups
                    .filter((id) => (!filter || id.toLowerCase().indexOf(filter) !== -1))
                    .filter((id) => this.userGroups.indexOf(id) === -1)
                    .sort(userGroupSortFn);
            },
            get selectedUserGroups() {
                if (this.userGroupFilterByEmail.length > 0) {
                    return this.userGroups
                        .filter((id) => this.userGroupFilterByEmail.indexOf(id) !== -1)
                        .sort(userGroupSortFn);
                }
                const filter = this.userGroupFilterStore.value.toLowerCase();
                return this.userGroups
                    .filter((id) => (!filter || id.toLowerCase().indexOf(filter) !== -1))
                    .sort(userGroupSortFn);
            },

            isNamedSetCategory: (c) => c.id >= 1001,

            redirectOptionsToggle: new FlagStore(true),
            searchedDomainCategoryIds: [],
            isolatedCategoriesFilterStore: new InputStore({ value: '' }),
            allIsolatedCategories: [],
            isolatedCategories: [],
            originalIsolatedCategories: [],
            get filteredIsolatedCategories() {
                const filter = this.isolatedCategoriesFilterStore.value.toLowerCase();
                return this.allIsolatedCategories
                    .filter(({ id, name }) => {
                        return this.searchedDomainCategoryIds.length > 0 ?
                            this.searchedDomainCategoryIds.includes(id) :
                            !filter || name.toLowerCase().includes(filter);
                    })
                    .sort((a, b) => {
                        if (this.isNamedSetCategory(a)) {
                            return -1;
                        }
                        if (this.isNamedSetCategory(b)) {
                            return 1;
                        }
                        return a.name.localeCompare(b.name);
                    });
            },
            get unselectedIsolatedCategories() {
                return this.filteredIsolatedCategories
                    .filter(({ id }) => this.isolatedCategories.indexOf(id) === -1);
            },
            get selectedIsolatedCategories() {
                return this.filteredIsolatedCategories
                    .filter(({ id }) => this.isolatedCategories.indexOf(id) !== -1);
            },
            get unselectedIsolatedCategoriesCount() {
                return this.allIsolatedCategories.length - this.isolatedCategories.length;
            },
            get selectedIsolatedCategoriesCount() {
                return this.isolatedCategories.length;
            },
            neverIsolate: new UrlBulkEditorCardStore(),

            securityOptionsToggle: new FlagStore(true),
            allowExitIsolationFromSafeState: true,
            allowExitIsolationFromSafeStateStore: new FlagStore(true),
            displayExitWarning: true,
            displayExitWarningStore: new FlagStore(true),
            exitWarningText: '',
            exitWarningTextStore: new InputStore(),

            dlpToggle: new FlagStore(true),
            dlpStore: new DataLossPreventionStore(),

            contentFilteringToggle: new FlagStore(true),
            contentFilteringStore: new ContentFilteringStore(),

            adminOptionsToggle: new FlagStore(true),
            alwaysRedirectIgnoringScan: true,
            alwaysRedirectIgnoringScanStore: new FlagStore(true),
            scanTimeout: DEFAULT_TIMEOUT,
            scanTimeoutStore: new SelectStore({ options: TIMEOUT_OPTIONS, value: DEFAULT_TIMEOUT }),
            neverIsolateAfterRedirect: true,
            neverIsolateAfterRedirectStore: new FlagStore(true),
            apiMethod: apiCall('/console/usergroups/search'),
            searchStatus: new AsyncActionStore(),
            debounceMs: 500,
            isValid: () => {
                return this.contentFilteringStore.urlFilterStore.allowedUrl
                    .urlBulkEditorStore.bulkEditorStore.isValid() &&
                this.contentFilteringStore.urlFilterStore.blockedUrl.urlBulkEditorStore
                    .bulkEditorStore.isValid() &&
                this.neverIsolate.urlBulkEditorStore.bulkEditorStore.isValid() &&
                this.nameStore.isValid;
            },

            hasChanged: () => {
                return !this.nameStore.equals(this.name)
                    || !this.enabledStore.equals(this.enabled)
                    || !compareArrays(this.userGroups, this.originalUserGroups)
                    || !compareArrays(this.isolatedCategories, this.originalIsolatedCategories)
                    || this.neverIsolate.hasChanged()
                    || !this.allowExitIsolationFromSafeStateStore.equals(this.allowExitIsolationFromSafeState)
                    || !this.displayExitWarningStore.equals(this.displayExitWarning)
                    || !this.exitWarningTextStore.equals(this.exitWarningText)
                    || this.dlpStore.hasChanged
                    || !this.alwaysRedirectIgnoringScanStore.equals(this.alwaysRedirectIgnoringScan)
                    || this.scanTimeoutStore.value !== this.scanTimeout
                    || !this.neverIsolateAfterRedirectStore.equals(this.neverIsolateAfterRedirect)
                    || this.contentFilteringStore.hasChanged();
            },
        }, data);
        this.nameStore.setValidator(() => !!this.nameStore.value);

        this.debouncedSearch = debounce(this.callApiMethodWithStatus, this.debounceMs);
        reaction(() => this.userGroupFilterStore.value, (newValue) => {
            if (!lenAwareEmailValidator(newValue)) {
                this.userGroupFilterByEmail.clear();
                return;
            }
            this.debouncedSearch();
        });
    }

    onSuccess = (items) => this.userGroupFilterByEmail.replace(items.map((group) => group.id));

    callApiMethodWithStatus = () => {
        this.searchStatus.setBase();
        this.searchStatus.setLoading(
            this.apiMethod({
                email: this.userGroupFilterStore.getValue()?.trim() ?? '',
                source: 'USER_CENTER'
            }).then(this.onSuccess)
        );
    }

    closeModal = action(() => {
        if (this.hasChanged() && !this.viewMode) {
            this.exitConfirmationModal.open();
        } else {
            this.modal.close();
        }
    });

    setValue = action((data = {}) => {
        this.id = data.id;
        this.isNew = !data.id;

        this.isDefault = !!data.isDefault;

        this.name = data.name;
        this.nameStore.setValue(data.name);
        this.nameStore.setTouched(true);
        this.enabled = data.enabled;
        this.enabledStore.setValue(data.enabled);

        this.userGroupFilterStore.setValue('');
        this.userGroups.replace(data.userGroups);
        this.originalUserGroups.replace(data.userGroups);

        this.isolatedCategoriesFilterStore.setValue('');
        this.searchedDomainCategoryIds.clear();
        this.isolatedCategories.replace(data.isolatedCategories);
        this.originalIsolatedCategories.replace(data.isolatedCategories);
        this.neverIsolate.setValue(data.neverIsolate);

        this.allowExitIsolationFromSafeState = data.allowExitIsolationFromSafeState;
        this.allowExitIsolationFromSafeStateStore.setValue(data.allowExitIsolationFromSafeState);
        this.displayExitWarning = data.displayExitWarning;
        this.displayExitWarningStore.setValue(data.displayExitWarning);
        this.exitWarningText = data.exitWarningText;
        this.exitWarningTextStore.setValue(data.exitWarningText);

        this.dlpStore.setFromObject(data.dlp || {});
        this.contentFilteringStore.setFromObject({
            ...data.contentFiltering,
            allCategories: [...this.allIsolatedCategories],
        });

        this.alwaysRedirectIgnoringScan = data.alwaysRedirectIgnoringScan;
        this.alwaysRedirectIgnoringScanStore.setValue(data.alwaysRedirectIgnoringScan);
        this.scanTimeout = data.scanTimeout;
        this.scanTimeoutStore.setValue(data.scanTimeout);
        this.neverIsolateAfterRedirect = data.neverIsolateAfterRedirect;
        this.neverIsolateAfterRedirectStore.setValue(data.neverIsolateAfterRedirect);
    });

    getValue() {
        return {
            name: this.nameStore.value,
            enabled: this.enabledStore.value,
            userGroups: [...this.userGroups],
            isolatedCategories: [...this.isolatedCategories],
            neverIsolate: this.neverIsolate.getValue(),
            allowExitIsolationFromSafeState: this.allowExitIsolationFromSafeStateStore.value,
            displayExitWarning: this.displayExitWarningStore.value,
            exitWarningText: this.exitWarningTextStore.value,
            dlp: this.dlpStore.toObject(),
            contentFiltering: this.contentFilteringStore.toObject(),
            alwaysRedirectIgnoringScan: this.alwaysRedirectIgnoringScanStore.value,
            scanTimeout: this.scanTimeoutStore.value,
            neverIsolateAfterRedirect: this.neverIsolateAfterRedirectStore.value,
        };
    }

    setMeta = action((userGroups, userGroupsError, categories) => {
        this.allUserGroups.replace(userGroups || []);
        this.userGroupsError = userGroupsError;
        this.allIsolatedCategories.replace(categories || []);
    });

    redirectAllCategories = action(() => {
        this.unselectedIsolatedCategories.forEach((c) => this.isolatedCategories.push(c.id));
    });

    removeAllCategories = action(() => {
        this.selectedIsolatedCategories.forEach((c) => this.isolatedCategories.remove(c.id));
    });

    expandAll = action(() => {
        this.policyDefinitionToggle.open();
        this.userGroupsToggle.open();
        this.redirectOptionsToggle.open();
        this.securityOptionsToggle.open();
        this.dlpToggle.open();
        this.contentFilteringToggle.open();
        this.adminOptionsToggle.open();
    });
}
